// Reports & Analytics — agency-wide rollups with inline SVG charts. Reads
// from the same client-side state the rest of the CRM uses (clients, leads,
// policies, commissions, invoices) so it works without a backend roundtrip.
// All charts are SVG-only — no chart library dependency.

import { useMemo, useState } from 'react';
import { TrendingUp, DollarSign, BarChart2, Users, FileText, Target, Calendar } from 'lucide-react';
import { useLocalStorage } from '../hooks.js';
import { calcTotals as invoiceTotals, computeStatus as invoiceStatus } from './InvoicesView.jsx';

const MONTH_KEYS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_KEYS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function fmtMoney(n) {
  if (Math.abs(n) >= 1000) return '$' + (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k';
  return '$' + Math.round(n).toLocaleString('en-US');
}

function fmtMoneyFull(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
}

// LOCAL 'YYYY-MM' for a timestamp/date. Slicing a raw ISO string takes the
// UTC month, so a payment/signup at 9pm on the last day of the month in Florida
// (UTC-4/-5) buckets into the NEXT month (feedback_local_day_not_utc). Pin
// date-only strings to local midnight first so they don't shift either.
function localMonthKey(d) {
  if (!d) return '';
  const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
  if (isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

function lastNMonths(n) {
  const out = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, year: d.getFullYear(), month: d.getMonth() });
  }
  return out;
}

function BarChart({ data, height = 180, color = '#00d4ff', formatY = fmtMoney, lang = 'en' }) {
  const max = Math.max(1, ...data.map(d => d.value));
  const monthLabels = lang === 'es' ? MONTH_KEYS_ES : MONTH_KEYS_EN;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height, paddingTop: 12, paddingBottom: 30, position: 'relative' }}>
      {data.map((d, i) => {
        const h = max > 0 ? (d.value / max) * (height - 60) : 0;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 4, position: 'relative' }} title={`${d.label}: ${formatY(d.value)}`}>
            <div style={{ fontSize: 9, color: 'var(--c-text2,#7aa3c0)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', marginBottom: 2 }}>
              {d.value > 0 ? formatY(d.value) : ''}
            </div>
            <div style={{
              width: '80%', height: Math.max(2, h), borderRadius: '4px 4px 0 0',
              background: `linear-gradient(180deg, ${color} 0%, ${color}88 100%)`,
              border: `1px solid ${color}55`,
              transition: 'height .35s ease',
              boxShadow: `0 0 12px ${color}33`,
            }} />
            <div style={{ fontSize: 10, color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', position: 'absolute', bottom: -22 }}>
              {monthLabels[d.month] || d.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HorizontalBarChart({ data, color = '#00d4ff', formatV = fmtMoney }) {
  const max = Math.max(1, ...data.map(d => d.value));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map((d, i) => {
        const pct = max > 0 ? (d.value / max) * 100 : 0;
        return (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--c-text,#c8dff0)', marginBottom: 3 }}>
              <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{d.label}</span>
              <span style={{ color: color, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{formatV(d.value)}</span>
            </div>
            <div style={{ height: 8, background: 'var(--c-surface2,#112538)', borderRadius: 4, overflow: 'hidden', border: '1px solid var(--c-border,#1a3550)' }}>
              <div style={{
                width: `${pct}%`, height: '100%',
                background: `linear-gradient(90deg, ${color} 0%, ${color}aa 100%)`,
                transition: 'width .35s ease',
              }} />
            </div>
          </div>
        );
      })}
      {data.length === 0 && <div style={{ fontSize: 12, color: 'var(--c-text3,#45688a)', textAlign: 'center', padding: '1rem' }}>No data</div>}
    </div>
  );
}

function KPI({ label, value, sub, icon: Icon, color = '#00d4ff' }) {
  return (
    <div className="dkt-card" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 4, borderLeft: `3px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</span>
        {Icon && <Icon size={14} style={{ color }} />}
      </div>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--c-text3,#45688a)' }}>{sub}</div>}
    </div>
  );
}

function SectionCard({ title, sub, children, icon: Icon }) {
  return (
    <div className="dkt-card" style={{ padding: '1.1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        {Icon && <Icon size={16} style={{ color: 'var(--c-cyan,#00d4ff)' }} />}
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontFamily: 'var(--font-display)' }}>{title}</div>
          {sub && <div style={{ fontSize: 10, color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)' }}>{sub}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

export function ReportsAnalyticsView({ clients = [], leads = [], policies = [], commissions = [], invoices, lang = 'en' }) {
  const es = lang === 'es';
  const [periodMonths, setPeriodMonths] = useState(12);
  // Invoices were hoisted to deskte-full when we made them server-backed
  // (2026-05-20); fall back to the legacy localStorage path for demo mode.
  const [localInvoices] = useLocalStorage('dkt6_invoices', []);
  const storedInvoices = invoices ?? localInvoices;

  const months = useMemo(() => lastNMonths(periodMonths), [periodMonths]);

  // Revenue by month — sum of PAID invoice payments by month
  const revenueByMonth = useMemo(() => {
    const buckets = Object.fromEntries(months.map(m => [m.key, 0]));
    for (const inv of storedInvoices) {
      const status = invoiceStatus(inv);
      if (status === 'void' || status === 'refunded' || status === 'draft') continue;
      for (const p of (inv.payments || [])) {
        const k = localMonthKey(p.recordedAt || p.date);
        if (k in buckets) buckets[k] += Number(p.amount || 0);
      }
    }
    return months.map(m => ({ label: m.key, month: m.month, value: buckets[m.key] }));
  }, [storedInvoices, months]);

  // Commission by month
  const commissionByMonth = useMemo(() => {
    const buckets = Object.fromEntries(months.map(m => [m.key, 0]));
    for (const c of commissions) {
      // Mapped commissions expose `month` ('YYYY-MM') / `dueMonth`
      // ('YYYY-MM-DD'); payDate/date/createdAt never existed, so this chart
      // was always empty. Bucket on the real field.
      const k = c.month || String(c.dueMonth || '').slice(0, 7);
      if (k in buckets) buckets[k] += Number(c.amount || 0);
    }
    return months.map(m => ({ label: m.key, month: m.month, value: buckets[m.key] }));
  }, [commissions, months]);

  // New clients by month
  const newClientsByMonth = useMemo(() => {
    const buckets = Object.fromEntries(months.map(m => [m.key, 0]));
    for (const c of clients) {
      const k = localMonthKey(c.createdAt);
      if (k in buckets) buckets[k] += 1;
    }
    return months.map(m => ({ label: m.key, month: m.month, value: buckets[m.key] }));
  }, [clients, months]);

  // Lead conversion: leads created vs converted, in the period
  const leadConversion = useMemo(() => {
    const startKey = months[0]?.key || '0000-00';
    let total = 0; let converted = 0;
    for (const l of leads) {
      const k = String(l.createdAt || '').slice(0, 7);
      if (k < startKey) continue;
      total += 1;
      if (l.status === 'converted') converted += 1;
    }
    const pct = total > 0 ? Math.round((converted / total) * 100) : 0;
    return { total, converted, pct };
  }, [leads, months]);

  // Top 5 clients by total premium across active policies
  const topClients = useMemo(() => {
    const totals = new Map();
    for (const p of policies) {
      if (p.status === 'cancelled' || p.status === 'inactive') continue;
      const c = clients.find(cl => cl.id === p.clientId);
      if (!c) continue;
      const monthly = Number(p.monthlyPremium || 0);
      const annual = monthly * 12;
      totals.set(c.id, (totals.get(c.id) || 0) + annual);
    }
    return [...totals.entries()]
      .map(([id, value]) => {
        const c = clients.find(x => x.id === id);
        return { label: c ? `${c.firstName} ${c.lastName}` : id, value };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [policies, clients]);

  // Active policies by type
  const policiesByType = useMemo(() => {
    const counts = new Map();
    for (const p of policies) {
      if (p.status === 'cancelled' || p.status === 'inactive') continue;
      const k = p.type || 'other';
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    return [...counts.entries()]
      .map(([k, v]) => ({ label: k.charAt(0).toUpperCase() + k.slice(1), value: v }))
      .sort((a, b) => b.value - a.value);
  }, [policies]);

  // KPI: lifetime revenue (paid invoices)
  const lifetimeRevenue = useMemo(() => {
    let s = 0;
    for (const inv of storedInvoices) {
      const t = invoiceTotals(inv);
      s += t.paid;
    }
    return s;
  }, [storedInvoices]);

  // KPI: outstanding revenue
  const outstanding = useMemo(() => {
    let s = 0;
    for (const inv of storedInvoices) {
      const status = invoiceStatus(inv);
      if (status === 'void' || status === 'refunded' || status === 'draft' || status === 'paid') continue;
      const t = invoiceTotals(inv);
      s += Math.max(0, t.balance);
    }
    return s;
  }, [storedInvoices]);

  // KPI: lifetime commissions
  const lifetimeCommissions = useMemo(() => commissions.reduce((s, c) => s + Number(c.amount || 0), 0), [commissions]);

  // Monthly recurring revenue estimate (from active policies)
  const mrr = useMemo(() => policies
    .filter(p => p.status !== 'cancelled' && p.status !== 'inactive')
    .reduce((s, p) => s + Number(p.monthlyPremium || 0), 0), [policies]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.5rem' }}>
        <div>
          <div className="dkt-sec-title">
            <BarChart2 size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
            {es ? 'Reportes y Análisis' : 'Reports & Analytics'}
          </div>
          <div className="dkt-sec-sub">{es ? 'Resumen de ingresos, comisiones y conversión' : 'Revenue, commissions and conversion overview'}</div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {[6, 12, 24].map((n) => (
            <button key={n} onClick={() => setPeriodMonths(n)}
              className={`dkt-tab${periodMonths === n ? ' on' : ''}`}>
              {n}m
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '.75rem' }}>
        <KPI label={es ? 'Ingresos totales' : 'Lifetime revenue'} value={fmtMoneyFull(lifetimeRevenue)} sub={es ? 'Facturas pagadas' : 'Paid invoices'} icon={DollarSign} color="#00ff88" />
        <KPI label={es ? 'Por cobrar' : 'Outstanding'} value={fmtMoneyFull(outstanding)} sub={es ? 'Pendiente de pago' : 'Awaiting payment'} icon={TrendingUp} color="#ffb700" />
        <KPI label={es ? 'Ingresos recurrentes' : 'MRR estimate'} value={fmtMoneyFull(mrr)} sub={es ? 'Por mes (pólizas activas)' : 'Per month (active policies)'} icon={Calendar} color="#00d4ff" />
        <KPI label={es ? 'Comisiones totales' : 'Lifetime commissions'} value={fmtMoneyFull(lifetimeCommissions)} sub={es ? 'Acumulado' : 'All-time'} icon={Target} color="#a78bfa" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(380px,1fr))', gap: '1rem' }}>
        <SectionCard title={es ? 'Ingresos por mes' : 'Revenue by month'} sub={es ? 'Pagos de facturas' : 'Invoice payments'} icon={DollarSign}>
          <BarChart data={revenueByMonth} color="#00ff88" lang={lang} />
        </SectionCard>

        <SectionCard title={es ? 'Comisiones por mes' : 'Commissions by month'} icon={Target}>
          <BarChart data={commissionByMonth} color="#a78bfa" lang={lang} />
        </SectionCard>

        <SectionCard title={es ? 'Nuevos clientes por mes' : 'New clients per month'} icon={Users}>
          <BarChart data={newClientsByMonth} color="#00d4ff" formatY={(v) => String(Math.round(v))} lang={lang} />
        </SectionCard>

        <SectionCard title={es ? 'Conversión de leads' : 'Lead conversion'} icon={TrendingUp}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0' }}>
            <div style={{ position: 'relative', width: 110, height: 110 }}>
              <svg viewBox="0 0 36 36" width="110" height="110" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="18" cy="18" r="15.915" fill="none" stroke="var(--c-border,#1a3550)" strokeWidth="3" />
                <circle cx="18" cy="18" r="15.915" fill="none" stroke="#00ff88" strokeWidth="3"
                  strokeDasharray={`${leadConversion.pct} ${100 - leadConversion.pct}`} strokeLinecap="round" />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, fontFamily: 'var(--font-display)', color: 'var(--c-white,#eef6ff)' }}>{leadConversion.pct}%</div>
                <div style={{ fontSize: 9, color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)' }}>{es ? 'CONVERTIDO' : 'CONVERTED'}</div>
              </div>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 12, color: 'var(--c-text2,#7aa3c0)' }}>
                <span style={{ color: '#00ff88', fontWeight: 700 }}>{leadConversion.converted}</span> {es ? 'convertidos' : 'converted'} / <span style={{ fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>{leadConversion.total}</span> {es ? 'leads totales' : 'total leads'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--c-text3,#45688a)' }}>
                {es ? `Período: últimos ${periodMonths} meses` : `Period: last ${periodMonths} months`}
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard title={es ? 'Top 5 clientes (anual)' : 'Top 5 clients (annualized)'} icon={Users}>
          <HorizontalBarChart data={topClients} color="#00d4ff" />
        </SectionCard>

        <SectionCard title={es ? 'Pólizas activas por tipo' : 'Active policies by type'} icon={FileText}>
          <HorizontalBarChart data={policiesByType} color="#ffb700" formatV={(v) => String(v)} />
        </SectionCard>
      </div>
    </div>
  );
}
